import { lazy, Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import UnknownRoute from '@/pages/UnknownRoute';
import { AuthProvider } from '@/lib/AuthContext';
import ScrollToTop from './components/ScrollToTop';
// Add page imports here
import Home from '@/pages/Home';
import AppLayout from '@/components/yts/AppLayout';
import BottomNav from '@/components/yts/BottomNav';
import ProtectedRoute from '@/components/ProtectedRoute';
import { ExploreProvider } from '@/lib/ExploreContext';
import { SavedProvider } from '@/lib/SavedContext';
import { CommunityHeartProvider } from '@/lib/CommunityHeartContext';
import { DetailOverlayProvider } from '@/lib/DetailOverlayContext';
import ListingDetailOverlay from '@/components/yts/ListingDetailOverlay';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { ThemeProvider } from '@/lib/theme';
import { LangProvider, useLang } from '@/lib/i18n';
import SiteTour from '@/components/yts/SiteTour';
import WelcomeTour from '@/components/yts/WelcomeTour';
import SiteEngagement from '@/components/yts/SiteEngagement';
import SavedSnapshot from '@/components/yts/SavedSnapshot';
import AffiliateAttribution from '@/components/AffiliateAttribution';

// Every page except the landing page loads on demand, so a first visit downloads
// only the code it needs (the map, charts and admin tools stay out of the way).
const MapPage = lazy(() => import('@/pages/MapPage'));
const DayPlanner = lazy(() => import('@/pages/DayPlanner'));
const SpanishLanding = lazy(() => import('@/pages/SpanishLanding'));
const About = lazy(() => import('@/pages/About'));
const Contact = lazy(() => import('@/pages/Contact'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Terms = lazy(() => import('@/pages/Terms'));
const EventsPage = lazy(() => import('@/pages/EventsPage'));
const ListingDetail = lazy(() => import('@/pages/ListingDetail'));
const BusinessPage = lazy(() => import('@/pages/BusinessPage'));
const ExperienceDetail = lazy(() => import('@/pages/ExperienceDetail'));
const BusinessDashboard = lazy(() => import('@/pages/BusinessDashboard'));
const ForBusiness = lazy(() => import('@/pages/ForBusiness'));
const Advertise = lazy(() => import('@/pages/Advertise'));
const AdStore = lazy(() => import('@/pages/AdStore'));
const AdvertiseDashboard = lazy(() => import('@/pages/AdvertiseDashboard'));
const Saved = lazy(() => import('@/pages/Saved'));
const CollectionPage = lazy(() => import('@/pages/CollectionPage'));
const Magazine = lazy(() => import('@/pages/Magazine'));
const ArchiveDetail = lazy(() => import('@/pages/Archive').then((module) => ({ default: module.ArchiveDetail })));
const StoryDetail = lazy(() => import('@/pages/StoryDetail'));
const Faq = lazy(() => import('@/pages/Faq'));
const VisitorServices = lazy(() => import('@/pages/VisitorServices'));
const CruisersWelcome = lazy(() => import('@/pages/CruisersWelcome'));
const GuidePage = lazy(() => import('@/pages/GuidePage'));
const DistrictsIndex = lazy(() => import('@/pages/DistrictsIndex'));
const DistrictPage = lazy(() => import('@/pages/DistrictPage'));
const MallDirectory = lazy(() => import('@/pages/MallDirectory'));
const Admin = lazy(() => import('@/pages/Admin'));
const Login = lazy(() => import('@/pages/Login'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const Hotels = lazy(() => import('@/pages/Hotels'));
const HotelDetail = lazy(() => import('@/pages/HotelDetail'));
const Auto = lazy(() => import('@/pages/Auto'));
const AutoSubmit = lazy(() => import('@/pages/AutoSubmit'));
const AutoDetail = lazy(() => import('@/pages/AutoDetail'));
const PerfumeriaPRShop = lazy(() => import('@/pages/PerfumeriaPRShop'));
const PerfumeriaProductPage = lazy(() => import('@/pages/PerfumeriaProductPage'));
const Studios = lazy(() => import('@/pages/Studios'));
const LGBTQGuide = lazy(() => import('@/pages/LGBTQGuide'));
const AfterDark = lazy(() => import('@/pages/AfterDark'));
const Register = lazy(() => import('@/pages/Register'));
const BadBunnyGuide = lazy(() => import('@/pages/BadBunnyGuide'));
const LaGabrielaPage = lazy(() => import('@/pages/LaGabrielaPage'));
const ClassicDirectory = lazy(() => import('@/pages/ClassicDirectory'));
const ClassicBusinessPage = lazy(() => import('@/pages/ClassicBusinessPage'));
const ClassicMapPage = lazy(() => import('@/pages/ClassicMapPage'));
const ClassicMallsIndex = lazy(() => import('@/classic/ClassicMallsIndex'));
const ClassicMallDirectory = lazy(() => import('@/classic/ClassicMallDirectory'));
const JobsPortal = lazy(() => import('@/pages/JobsPortal').then((module) => ({ default: module.JobsPortal })));
const JobsJobDetail = lazy(() => import('@/pages/JobsPortal').then((module) => ({ default: module.JobsJobDetail })));
const JobsForEmployers = lazy(() => import('@/pages/JobsPortal').then((module) => ({ default: module.JobsForEmployers })));

// One loading state for the app shell and lazy pages, announced to screen readers.
const PageFallback = () => {
  const { t } = useLang();
  return (
    <div className="fixed inset-0 flex items-center justify-center" role="status" aria-live="polite">
      <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin" aria-hidden="true"></div>
      <span className="sr-only">{t('common.loading')}</span>
    </div>
  );
};

const GUIDE_LOCATIONS = ["rio piedras","santurce","condado","hato rey","carolina","bayamon","guaynabo","san juan","old san juan","miramar","ocean park","pinones","caguas","catano","cupey","toa baja","puerto rico"];
const GUIDE_CANONICAL_LOCATIONS = ["RioPiedras","Santurce","Condado","HatoRey","Carolina","Bayamon","Guaynabo","SanJuan","OldSanJuan","Miramar","OceanPark","Pinones","Caguas","Catano","Cupey","ToaBaja","PuertoRico"];
const GUIDE_CATEGORIES = ["restaurants","bars nightlife","things to do","beaches","shopping","culture","services"];
const GUIDE_CATEGORY_SLUGS = GUIDE_CATEGORIES.map((category) => category.replace(/\s+/g, "-"));

// The standalone booking directory used to live at /classic; every old link and
// search result now lands on its Book It Now address instead.
const BookItNowRedirect = () => {
  const loc = useLocation();
  return <Navigate replace to={`/bookit${loc.pathname.replace(/^\/classic/, "")}${loc.search}${loc.hash}`} />;
};

const LegacyGuideRedirect = () => {
  const { slug, location, category } = useParams();
  const target = location || slug;
  const cleanTarget = decodeURIComponent(target || "").replace(/-/g, " ");
  const cleanCategory = category ? decodeURIComponent(category).replace(/-/g, " ") : "";
  return <Navigate replace to={`/${encodeURIComponent(cleanTarget)}${cleanCategory ? `/${encodeURIComponent(cleanCategory)}` : ""}`} />;
};

const AuthenticatedApp = () => {
  const loc = useLocation();
  const hideNav = [
    "/login",
    "/register",
    "/forgot-password",
    "/reset-password",
    "/admin",
    "/business/dashboard",
    "/classic",
    "/bookit",
    "/jobs",
    "/perfumeria-pr",
    "/after-dark",
  ].some((p) =>
    loc.pathname.startsWith(p)
  );

  // Public browsing never waits for authentication or redirects to a login.
  // Protected dashboard routes handle their own authentication independently.

  // Render the main app
  return (
    <ExploreProvider>
      <SavedProvider>
        <CommunityHeartProvider>
        <DetailOverlayProvider>
        <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/jobs" element={<JobsPortal />} />
          <Route path="/jobs/for-employers" element={<JobsForEmployers />} />
          <Route path="/jobs/:jobId" element={<JobsJobDetail />} />
          <Route element={<AppLayout />}>
            <Route path="/es" element={<SpanishLanding />} />
            <Route path="/" element={<Home />} />
            <Route path="/map" element={<MapPage />} />
            <Route path="/make-a-day" element={<DayPlanner />} />
            <Route path="/districts" element={<DistrictsIndex />} />
            <Route path="/plaza-las-americas" element={<MallDirectory mallId="plaza-las-americas" />} />
            <Route path="/san-patricio-plaza" element={<MallDirectory mallId="san-patricio-plaza" />} />
            <Route path="/santa-rosa-mall" element={<MallDirectory mallId="santa-rosa-mall" />} />
            <Route path="/plaza-del-sol" element={<MallDirectory mallId="plaza-del-sol" />} />
            <Route path="/mall-of-san-juan" element={<MallDirectory mallId="mall-of-san-juan" />} />
            <Route path="/districts/:slug" element={<DistrictPage />} />
            {GUIDE_LOCATIONS.map((location) => <Route key={location} path={`/${location}`} element={<GuidePage routeLocation={location} />} />)}
            {GUIDE_LOCATIONS.flatMap((location) => GUIDE_CATEGORIES.map((category) => (
              <Route key={`${location}-${category}`} path={`/${location}/${category}`} element={<GuidePage routeLocation={location} routeCategory={category} />}/>
            )))}
            {GUIDE_CANONICAL_LOCATIONS.map((location) => <Route key={`canonical-${location}`} path={`/${location}`} element={<GuidePage routeLocation={location} />} />)}
            {GUIDE_CANONICAL_LOCATIONS.flatMap((location) => GUIDE_CATEGORY_SLUGS.map((category) => (
              <Route key={`canonical-${location}-${category}`} path={`/${location}/${category}`} element={<GuidePage routeLocation={location} routeCategory={category} />}/>
            )))}
            <Route path="/guide/:location/:category" element={<LegacyGuideRedirect />} />
            <Route path="/guide/:slug" element={<LegacyGuideRedirect />} />
            <Route path="/about" element={<About />} />
            <Route path="/lgbtq" element={<LGBTQGuide />} />
            <Route path="/after-dark" element={<AfterDark />} />
            <Route path="/after-dark/:section" element={<AfterDark />} />
            <Route path="/bad-bunny-puerto-rico" element={<BadBunnyGuide />} />
            <Route path="/la-gabriela-rio-piedras" element={<LaGabrielaPage />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/hotels" element={<Hotels />} />
            <Route path="/hotels/:id" element={<HotelDetail />} />
            {/* The real estate section is retired; old links land on hotels. */}
            <Route path="/real-estate" element={<Navigate replace to="/hotels" />} />
            <Route path="/real-estate/*" element={<Navigate replace to="/hotels" />} />
            <Route path="/auto" element={<Auto />} />
            <Route path="/auto/submit" element={<AutoSubmit />} />
            <Route path="/auto/:id" element={<AutoDetail />} />
            <Route path="/planner" element={<Saved />} />
            <Route path="/saved" element={<Saved />} />
            <Route path="/business" element={<ForBusiness />} />
            <Route path="/advertise" element={<Advertise />} />
            <Route path="/advertise/store" element={<AdStore />} />
            <Route path="/explore/:slug" element={<CollectionPage />} />
            <Route path="/studios" element={<Studios />} />
            <Route path="/el-ensayo" element={<Navigate to="/elensayo" replace />} />
            <Route path="/mencho" element={<Navigate to="/mensho" replace />} />
            <Route path="/magazine" element={<Magazine />} />
            <Route path="/magazine/:slug" element={<Magazine />} />
            <Route path="/historia/:slug" element={<ArchiveDetail />} />
            <Route path="/story/:id" element={<StoryDetail />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/visitor-services" element={<VisitorServices />} />
            <Route path="/cruise" element={<CruisersWelcome />} />
            <Route path="/experience/:id" element={<ExperienceDetail />} />
            <Route path="/perfumeria-pr" element={<PerfumeriaPRShop />} />
            <Route path="/perfumeria-pr/perfume/:slug" element={<PerfumeriaProductPage />} />
          </Route>
          <Route path="/bookit" element={<ClassicDirectory />} />
          <Route path="/bookit/map" element={<ClassicMapPage />} />
          <Route path="/bookit/events" element={<EventsPage />} />
          <Route path="/bookit/districts" element={<DistrictsIndex />} />
          <Route path="/bookit/districts/:slug" element={<DistrictPage />} />
          <Route path="/bookit/business/:slug" element={<ClassicBusinessPage />} />
          <Route path="/bookit/malls" element={<ClassicMallsIndex />} />
          <Route path="/bookit/malls/:mallId" element={<ClassicMallDirectory />} />
          <Route path="/classic/*" element={<BookItNowRedirect />} />
          <Route path="/listing/:id" element={<ListingDetail />} />
          <Route path="/:slug" element={<BusinessPage />} />
          <Route element={<ProtectedRoute unauthenticatedElement={<Navigate to="/login" replace />} />}>
            <Route path="/admin" element={<Admin />} />
            <Route path="/business/dashboard" element={<BusinessDashboard />} />
            <Route path="/advertise/dashboard" element={<AdvertiseDashboard />} />
          </Route>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="*" element={<UnknownRoute />} />
        </Routes>
        </Suspense>
        <ListingDetailOverlay />
        <SavedSnapshot />
        {!hideNav && <BottomNav />}
        </DetailOverlayProvider>
        </CommunityHeartProvider>
      </SavedProvider>
    </ExploreProvider>
  );
};


function App() {

  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <ThemeProvider>
          <LangProvider>
            <Router>
              <ScrollToTop />
              <AffiliateAttribution />
              <AuthenticatedApp />
              <WelcomeTour />
              <SiteTour />
              <SiteEngagement />
            </Router>
          </LangProvider>
        </ThemeProvider>
        <Toaster />
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App